/**
 * StreakService - Manages daily streak tracking
 * 
 * Tracks consecutive days of learning activity and provides streak bonuses.
 */

import { storageService } from './StorageService';
import { configService } from './ConfigService';

class StreakService {
  /**
   * Record activity for today and update streak
   * Should be called when user completes meaningful activity
   */
  recordActivity() {
    const streakData = storageService.getStreakData();
    const today = this.getDateString(new Date());
    const lastActiveDate = streakData.lastActiveDate;
    
    // If already active today, no need to update
    if (lastActiveDate === today) {
      return {
        streakUpdated: false,
        currentStreak: streakData.currentStreak,
        longestStreak: streakData.longestStreak
      };
    }
    
    let newStreak = streakData.currentStreak;
    let streakBroken = false;
    let streakExtended = false;
    
    if (lastActiveDate) {
      const daysSinceLastActive = this.getDaysDifference(lastActiveDate, today);
      
      if (daysSinceLastActive === 1) {
        // Consecutive day - extend streak
        newStreak++;
        streakExtended = true;
      } else if (daysSinceLastActive > 1) {
        // Streak broken - reset to 1
        newStreak = 1;
        streakBroken = true;
      }
      // daysSinceLastActive === 0 means same day (shouldn't happen due to check above)
    } else {
      // First activity ever
      newStreak = 1;
    }
    
    // Update longest streak
    const longestStreak = Math.max(streakData.longestStreak, newStreak);
    
    // Update total days active
    const totalDaysActive = (streakData.totalDaysActive || 0) + 1;
    
    // Save updated data
    storageService.updateStreakData({
      currentStreak: newStreak,
      longestStreak: longestStreak,
      lastActiveDate: today,
      totalDaysActive: totalDaysActive
    });
    
    // Award streak bonus points if streak extended
    let bonusPoints = 0;
    if (streakExtended) {
      const gamification = configService.getGamification();
      bonusPoints = gamification.streakBonus || 10;
      storageService.addPoints(bonusPoints);
    }
    
    return {
      streakUpdated: true,
      currentStreak: newStreak,
      longestStreak: longestStreak,
      streakBroken: streakBroken,
      streakExtended: streakExtended,
      bonusPoints: bonusPoints,
      totalDaysActive: totalDaysActive
    };
  }

  /**
   * Get current streak status
   */
  getStreakStatus() {
    const streakData = storageService.getStreakData();
    const today = this.getDateString(new Date());
    
    // Check if streak is still active (was active today or yesterday)
    let isActive = false;
    let willExpireToday = false;
    
    if (streakData.lastActiveDate) {
      const daysSinceLastActive = this.getDaysDifference(streakData.lastActiveDate, today);
      isActive = daysSinceLastActive <= 1;
      willExpireToday = daysSinceLastActive === 1;
    }
    
    return {
      currentStreak: isActive ? streakData.currentStreak : 0,
      longestStreak: streakData.longestStreak,
      lastActiveDate: streakData.lastActiveDate,
      totalDaysActive: streakData.totalDaysActive || 0,
      isActive: isActive,
      willExpireToday: willExpireToday,
      activeToday: streakData.lastActiveDate === today
    };
  }

  /**
   * Check if user needs to maintain their streak today
   */
  needsActivityToday() {
    const status = this.getStreakStatus();
    return status.willExpireToday && !status.activeToday;
  }

  /**
   * Get streak milestones
   */
  getStreakMilestones() {
    const currentStreak = this.getStreakStatus().currentStreak;
    
    const milestones = [
      { days: 3, name: '3 Day Streak', icon: '🔥', achieved: currentStreak >= 3 },
      { days: 7, name: 'Week Warrior', icon: '🔥', achieved: currentStreak >= 7 },
      { days: 14, name: '2 Week Champion', icon: '🔥🔥', achieved: currentStreak >= 14 },
      { days: 30, name: 'Month Master', icon: '🔥🔥🔥', achieved: currentStreak >= 30 },
      { days: 50, name: 'Halfway Hero', icon: '⚡', achieved: currentStreak >= 50 },
      { days: 100, name: 'Century Coder', icon: '👑', achieved: currentStreak >= 100 },
      { days: 365, name: 'Year Legend', icon: '🏆', achieved: currentStreak >= 365 }
    ];
    
    // Find next milestone
    const nextMilestone = milestones.find(m => !m.achieved);
    
    return {
      milestones,
      nextMilestone,
      daysToNextMilestone: nextMilestone ? nextMilestone.days - currentStreak : 0
    };
  }

  /**
   * Get streak calendar for the current month
   */
  getStreakCalendar(year, month) {
    const activeDays = storageService.get('active_days', []);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const calendar = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateString = this.getDateString(date);
      const isActive = activeDays.includes(dateString);
      const isFuture = date > new Date();
      const isToday = dateString === this.getDateString(new Date());
      
      calendar.push({
        day,
        date: dateString,
        isActive,
        isFuture,
        isToday
      });
    }
    
    return calendar;
  }

  /**
   * Record a day as active (for calendar tracking)
   */
  markDayActive(dateString = null) {
    const date = dateString || this.getDateString(new Date());
    const activeDays = storageService.get('active_days', []);
    
    if (!activeDays.includes(date)) {
      activeDays.push(date);
      // Keep only last 365 days to prevent storage bloat
      if (activeDays.length > 365) {
        activeDays.shift();
      }
      storageService.set('active_days', activeDays);
    }
  }

  /**
   * Get statistics about activity patterns
   */
  getActivityStats() {
    const activeDays = storageService.get('active_days', []);
    const streakData = storageService.getStreakData();
    
    // Get activity for last 30 days
    const last30Days = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateString = this.getDateString(date);
      last30Days.push({
        date: dateString,
        active: activeDays.includes(dateString)
      });
    }
    
    const activeLast30 = last30Days.filter(d => d.active).length;
    
    // Calculate consistency percentage
    const consistency = Math.round((activeLast30 / 30) * 100);
    
    return {
      totalDaysActive: streakData.totalDaysActive || 0,
      activeLast30Days: activeLast30,
      consistency: consistency,
      last30Days: last30Days,
      averagePerWeek: Math.round((activeLast30 / 30) * 7 * 10) / 10
    };
  }

  /**
   * Helper: Get date string in YYYY-MM-DD format
   */
  getDateString(date) {
    return date.toISOString().split('T')[0];
  }

  /**
   * Helper: Get difference in days between two date strings
   */
  getDaysDifference(dateStr1, dateStr2) {
    const date1 = new Date(dateStr1);
    const date2 = new Date(dateStr2);
    const diffTime = Math.abs(date2 - date1);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Reset streak (for testing)
   */
  resetStreak() {
    storageService.updateStreakData({
      currentStreak: 0,
      longestStreak: storageService.getStreakData().longestStreak,
      lastActiveDate: null,
      totalDaysActive: storageService.getStreakData().totalDaysActive
    });
  }
}

// Export singleton instance
export const streakService = new StreakService();
export default streakService;